import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Folder, FileText,
  Plus, Upload as UploadIcon, Search,
  RefreshCw, Grid, List, Home, MoreVertical, ArrowLeft,
} from 'lucide-react';
import { api, getAccessToken } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { useUploader } from '../hooks/useUploader';
import { formatBytes, formatDate, isImage } from '../lib/utils';
import type { Node } from '@hongweibing-netdisk/shared';
import toast from 'react-hot-toast';
import { PreviewModal } from '../components/PreviewModal';
import { ShareDialog } from '../components/ShareDialog';
import { UploadTray } from '../components/UploadTray';
import { MoveDialog } from '../components/MoveDialog';
import { NodeActions } from '../components/NodeActions';
import { FileThumb } from '../components/FileThumb';
import { fileVisual } from '../lib/fileVisual';
import { TextGenerateEffect } from '../components/aceternity/TextGenerateEffect';
import { FileUpload } from '../components/aceternity/FileUpload';
import { MovingBorder } from '../components/aceternity/MovingBorder';
import { AnimatedTabs } from '../components/aceternity/AnimatedTabs';
import { CardSpotlight } from '../components/aceternity/CardSpotlight';
import { HoverBorderGradient } from '../components/aceternity/HoverBorderGradient';
import { Lamp } from '../components/aceternity/Lamp';
import { listContainer, listItem, scaleIn, backdrop, spring, snappySpring } from '../lib/motion';

export function Files() {
  const { folderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const uploader = useUploader();
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [preview, setPreview] = useState<Node | null>(null);
  const [shareNode, setShareNode] = useState<Node | null>(null);
  const [moveNode, setMoveNode] = useState<Node | null>(null);
  const [actionNode, setActionNode] = useState<Node | null>(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameNode, setRenameNode] = useState<Node | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['nodes', folderId || 'root', search],
    queryFn: () => api.nodes.list({ parentId: folderId || null, q: search || undefined }),
  });

  useEffect(() => {
    setSelected([]);
  }, [folderId]);

  const folder = useMemo(() => (data?.items ?? []).filter((n) => n.isFolder), [data]);
  const files = useMemo(() => (data?.items ?? []).filter((n) => !n.isFolder), [data]);
  const total = data?.total ?? 0;

  const isSelected = (id: string) => selected.includes(id);

  const openFolder = (id: string) => navigate(`/files/${id}`);
  const goHome = () => navigate('/files');

  const downloadNode = (id: string) => {
    const url = api.nodes.downloadUrl(id);
    const token = getAccessToken();
    const a = document.createElement('a');
    a.href = `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token || '')}`;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => a.remove(), 1000);
  };

  const openPreview = (n: Node) => {
    if (n.isFolder) { openFolder(n.id); return; }
    setPreview(n);
  };

  const doRename = async () => {
    if (!renameNode || !renameValue.trim()) return;
    await api.nodes.rename(renameNode.id, renameValue.trim());
    toast.success('已重命名');
    setRenameNode(null);
    refetch();
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    await api.nodes.createFolder({ parentId: folderId || null, name: newFolderName.trim() });
    setNewFolderName('');
    setShowNewFolder(false);
    refetch();
  };

  const deleteNode = async (n: Node) => {
    if (!confirm(`将「${n.name}」移入回收站？`)) return;
    await api.nodes.toTrash(n.id);
    toast.success('已移入回收站');
    setSelected((s) => s.filter((x) => x !== n.id));
    refetch();
  };

  return (
    <div className="h-full flex flex-col animate-fadeIn relative">
      <Lamp />
      {/* 页头 */}
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono tracking-[0.35em] text-primary/80">MY STORAGE</span>
            <span className="h-px w-10 bg-gradient-to-r from-primary to-transparent" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-black neon-text tracking-tight mt-0.5">
            <TextGenerateEffect words="我 的 文 件" duration={0.5} />
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-card/60 border border-border-soft">
            <span className="text-[11px] text-text-muted">文件夹</span>
            <span className="text-sm font-bold text-accent-amber tabular-nums">{folder.length}</span>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-card/60 border border-border-soft">
            <span className="text-[11px] text-text-muted">文件</span>
            <span className="text-sm font-bold text-accent-cyan tabular-nums">{files.length}</span>
          </div>
        </div>
      </div>

      {/* 工具栏 */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <motion.button whileTap={{ scale: 0.94 }} onClick={goHome} className="flex items-center gap-1.5 text-text-muted hover:text-text px-2.5 py-2 rounded-xl hover:bg-card/70 whitespace-nowrap text-sm font-medium flex-shrink-0 transition-colors">
          <Home className="w-4 h-4" /> <span className="hidden sm:inline">全部文件</span>
        </motion.button>
        {folderId && (
          <motion.button whileTap={{ scale: 0.94 }} onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-text-muted hover:text-text px-2.5 py-2 rounded-xl hover:bg-card/70 text-sm font-medium flex-shrink-0 transition-colors">
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">返回</span>
          </motion.button>
        )}
        <div className="flex-1" />
        <div className="flex items-center gap-2 flex-shrink-0">
          <AnimatedTabs
            size="sm"
            active={view}
            onChange={(id) => setView(id as 'grid' | 'list')}
            tabs={[
              { id: 'grid', label: '网格', icon: Grid },
              { id: 'list', label: '列表', icon: List },
            ]}
          />
          <motion.button onClick={() => refetch()} whileTap={{ rotate: 180, scale: 0.9 }} transition={snappySpring} className="p-2 rounded-lg text-text-muted hover:text-text hover:bg-card border border-border-soft transition-colors" aria-label="刷新"><RefreshCw className="w-4 h-4" /></motion.button>
        </div>
        <div className="relative flex-1 min-w-[140px] order-last sm:order-none w-full sm:w-auto group">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted group-focus-within:text-primary transition-colors" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="搜索文件..."
            className="w-full pl-9 pr-3 py-2 bg-card/70 border border-border-soft rounded-xl text-sm placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary/60 transition-all" />
        </div>
        <HoverBorderGradient
          onClick={() => setShowNewFolder(true)}
          containerClassName="flex-shrink-0"
          className="px-3 py-2 text-sm font-medium text-text flex items-center gap-1.5"
          aria-label="新建文件夹"
        >
          <Plus className="w-4 h-4" /> <span className="hidden sm:inline">新建文件夹</span>
        </HoverBorderGradient>
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => { if (e.target.files?.length) uploader.addTasks(e.target.files, folderId || null); e.target.value = ''; }} />
        <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.95 }} transition={snappySpring} onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1.5 px-4 py-2 brand-gradient text-white text-sm font-bold rounded-xl flex-shrink-0 glow-primary">
          <UploadIcon className="w-4 h-4" /> 上传
        </motion.button>
      </div>

      {/* 存储使用量 */}
      <div className="mb-4 p-3 rounded-2xl bg-card/60 border border-border-soft relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-0.5 brand-gradient opacity-60" />
        <div className="flex justify-between text-xs mb-2">
          <span className="text-text-muted font-mono tracking-wider">STORAGE</span>
          <span className="tabular-nums font-semibold text-text">{formatBytes(user?.usedBytes ?? 0)} <span className="text-text-muted">/ {formatBytes(user?.storageQuotaBytes ?? 0)}</span></span>
        </div>
        <div className="h-2 bg-bg-secondary rounded-full overflow-hidden relative">
          <div className="absolute inset-0 dot-grid opacity-40" />
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, ((user?.usedBytes ?? 0) / (user?.storageQuotaBytes ?? 1)) * 100)}%` }}
            transition={{ type: 'spring', stiffness: 120, damping: 24, delay: 0.15 }}
            className="h-full brand-gradient rounded-full glow-primary relative"
          />
        </div>
      </div>

      {/* 内容 */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className={`${view === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3' : 'space-y-2'}`}>
            {Array.from({ length: view === 'grid' ? 12 : 6 }).map((_, i) => (
              <div key={i} className={`skeleton ${view === 'grid' ? 'h-28' : 'h-14'}`} />
            ))}
          </div>
        ) : total === 0 ? (
          <motion.div variants={scaleIn} initial="hidden" animate="show" className="flex flex-col items-center justify-center min-h-[16rem] gap-4 py-6">
            <div className="flex items-center gap-2 text-text-muted">
              <Folder className="w-5 h-5 text-primary/60" />
              <p className="font-medium">此目录为空</p>
            </div>
            <div className="w-full max-w-lg">
              <FileUpload
                hint="拖拽文件到此处，或点击选择"
                onChange={(newFiles) => uploader.addTasks(newFiles, folderId || null)}
              />
            </div>
          </motion.div>
        ) : (
          <>
            {folder.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2.5">
                  <Folder className="w-3.5 h-3.5 text-accent-amber" />
                  <p className="text-xs uppercase tracking-[0.3em] font-bold text-accent-amber">文件夹</p>
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-md bg-accent-amber/15 text-accent-amber">{folder.length}</span>
                  <span className="h-px flex-1 bg-gradient-to-r from-accent-amber/40 to-transparent" />
                </div>
                <motion.div variants={listContainer} initial="hidden" animate="show" className={`group/grid ${view === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3' : 'space-y-1'}`}>
                  {folder.map((n) => (
                    <motion.div key={n.id}
                      variants={listItem}
                      layout
                      whileHover={{ y: -5 }}
                      whileTap={{ scale: 0.97 }}
                      transition={spring}
                      onClick={() => openFolder(n.id)}
                      className={`group relative rounded-2xl border cursor-pointer bg-card/80 border-border-soft hover:border-red-500/40 shadow-sm hover:shadow-2xl transition-all duration-300 group-hover/grid:opacity-40 hover:!opacity-100 ${isSelected(n.id) ? 'ring-2 ring-primary/50' : ''}`}>
                      {/* 文件夹封面区 */}
                      <div className="relative aspect-[4/3] flex items-center justify-center bg-gradient-to-br from-red-500/8 to-transparent overflow-hidden">
                        <motion.span whileHover={{ scale: 1.12, rotate: -5 }} transition={snappySpring} className="flex items-center justify-center w-16 h-16 rounded-2xl bg-red-500/12">
                          <Folder className="w-8 h-8 text-red-500" fill="currentColor" fillOpacity={0.18} />
                        </motion.span>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className="px-3 py-2.5 text-left">
                        <p className="text-sm text-text truncate font-medium">{n.name}</p>
                        <p className="text-xs text-text-muted truncate mt-0.5">
                          {view === 'list' ? formatDate(n.updatedAt) : '文件夹'}
                        </p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setActionNode(n); }}
                        className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/40 backdrop-blur text-white hover:bg-black/60 active:scale-90 lg:opacity-0 lg:group-hover:opacity-100 transition-all touch-manipulation"
                        aria-label="更多操作">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            )}
            {files.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2.5">
                  <FileText className="w-3.5 h-3.5 text-accent-cyan" />
                  <p className="text-xs uppercase tracking-[0.3em] font-bold text-accent-cyan">文件</p>
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-md bg-accent-cyan/15 text-accent-cyan">{files.length}</span>
                  <span className="h-px flex-1 bg-gradient-to-r from-accent-cyan/40 to-transparent" />
                </div>
                <motion.div variants={listContainer} initial="hidden" animate="show" className={`group/grid ${view === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3' : 'space-y-1'}`}>
                  {files.map((n) => {
                    const vis = fileVisual(n.mimeType, n.name);
                    const isImg = isImage(n.mimeType);
                    const Icon = vis.Icon;
                    if (view === 'grid') {
                      return (
                        <motion.div key={n.id}
                          variants={listItem}
                          layout
                          whileHover={{ y: -5 }}
                          whileTap={{ scale: 0.97 }}
                          transition={spring}
                          onClick={() => openPreview(n)}
                          className={`group relative rounded-2xl border overflow-hidden cursor-pointer bg-card/80 border-border-soft hover:border-primary/40 shadow-sm hover:shadow-2xl transition-all duration-300 group-hover/grid:opacity-40 hover:!opacity-100 ${isSelected(n.id) ? 'ring-2 ring-primary/50' : ''}`}>
                          {/* 封面区 */}
                          <div className="relative aspect-[4/3] bg-bg-secondary/60 flex items-center justify-center overflow-hidden">
                            {isImg ? (
                              <FileThumb nodeId={n.id} alt={n.name} className="w-full h-full transition-transform duration-500 group-hover:scale-105" />
                            ) : (
                              <motion.span whileHover={{ scale: 1.12 }} transition={snappySpring} className={`flex items-center justify-center w-14 h-14 rounded-2xl ${vis.bg}`}>
                                <Icon className={`w-7 h-7 ${vis.color}`} />
                              </motion.span>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          {/* 信息区 */}
                          <div className="px-3 py-2.5 text-left">
                            <p className="text-sm text-text truncate" title={n.name}>{n.name}</p>
                            <p className="text-xs text-text-muted truncate mt-0.5">{formatBytes(n.sizeBytes)}</p>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); setActionNode(n); }}
                            className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/40 backdrop-blur text-white hover:bg-black/60 active:scale-90 lg:opacity-0 lg:group-hover:opacity-100 transition-all touch-manipulation"
                            aria-label="更多操作">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </motion.div>
                      );
                    }
                    return (
                      <motion.div key={n.id}
                        variants={listItem}
                        layout
                        whileHover={{ x: 3 }}
                        transition={spring}
                        onClick={() => openPreview(n)}
                        className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer bg-card/80 border-border-soft hover:border-primary/40 hover:bg-card transition-all duration-300 group-hover/grid:opacity-40 hover:!opacity-100 ${isSelected(n.id) ? 'ring-2 ring-primary/50' : ''}`}>
                        {isImg ? (
                          <FileThumb nodeId={n.id} alt={n.name} className="w-10 h-10 rounded-lg flex-shrink-0" />
                        ) : (
                          <span className={`flex items-center justify-center w-10 h-10 rounded-xl flex-shrink-0 ${vis.bg}`}>
                            <Icon className={`w-5 h-5 ${vis.color}`} />
                          </span>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-text truncate" title={n.name}>{n.name}</p>
                          <p className="text-xs text-text-muted truncate mt-0.5">{formatBytes(n.sizeBytes)}</p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); setActionNode(n); }}
                          className="p-1.5 rounded-lg text-text-muted hover:text-text hover:bg-bg-secondary active:scale-90 lg:opacity-0 lg:group-hover:opacity-100 transition-all touch-manipulation"
                          aria-label="更多操作">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </div>
            )}
          </>
        )}
      </div>

      <AnimatePresence>
        {showNewFolder && (
          <motion.div variants={backdrop} initial="hidden" animate="show" exit="exit" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowNewFolder(false)}>
            <motion.div variants={scaleIn} className="bg-card/95 backdrop-blur-xl border border-border-soft rounded-2xl p-5 w-full max-w-sm soft-shadow" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-text mb-4">新建文件夹</h3>
              <input autoFocus value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && createFolder()}
                placeholder="文件夹名称"
                className="w-full px-3 py-2.5 bg-bg/60 border border-border rounded-xl text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all mb-4" />
              <div className="flex justify-end gap-2">
                <motion.button whileTap={{ scale: 0.95 }} onClick={() => setShowNewFolder(false)} className="px-4 py-2 rounded-xl text-text-muted hover:bg-card transition-colors">取消</motion.button>
                <motion.button whileTap={{ scale: 0.95 }} onClick={createFolder} className="px-4 py-2 rounded-xl brand-gradient text-white soft-shadow hover:brightness-110 transition-all">创建</motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      <AnimatePresence>
        {renameNode && (
          <motion.div variants={backdrop} initial="hidden" animate="show" exit="exit" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setRenameNode(null)}>
            <motion.div variants={scaleIn} className="bg-card/95 backdrop-blur-xl border border-border-soft rounded-2xl p-5 w-full max-w-sm soft-shadow" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-text mb-4">重命名</h3>
              <input autoFocus value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && doRename()}
                placeholder={renameNode.name}
                className="w-full px-3 py-2.5 bg-bg/60 border border-border rounded-xl text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all mb-4" />
              <div className="flex justify-end gap-2">
                <motion.button whileTap={{ scale: 0.95 }} onClick={() => setRenameNode(null)} className="px-4 py-2 rounded-xl text-text-muted hover:bg-card transition-colors">取消</motion.button>
                <motion.button whileTap={{ scale: 0.95 }} onClick={doRename} className="px-4 py-2 rounded-xl brand-gradient text-white soft-shadow hover:brightness-110 transition-all">保存</motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {preview && <PreviewModal key="preview" node={preview} onClose={() => setPreview(null)} onDownload={() => downloadNode(preview.id)} onShare={() => { setShareNode(preview); setPreview(null); }} />}
      </AnimatePresence>
      <AnimatePresence>
        {shareNode && <ShareDialog key="share" node={shareNode} onClose={() => setShareNode(null)} />}
      </AnimatePresence>
      <AnimatePresence>
        {moveNode && (
          <MoveDialog
            key="move"
            node={moveNode}
            currentParentId={folderId || null}
            onClose={() => setMoveNode(null)}
            onDone={() => refetch()}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {actionNode && (
          <NodeActions
            key="actions"
            node={actionNode}
            onClose={() => setActionNode(null)}
            onDownload={() => downloadNode(actionNode.id)}
            onShare={() => setShareNode(actionNode)}
            onMove={() => setMoveNode(actionNode)}
            onRename={() => { setRenameValue(actionNode.name); setRenameNode(actionNode); }}
            onDelete={() => deleteNode(actionNode)}
          />
        )}
      </AnimatePresence>
      <UploadTray />
    </div>
  );
}
